/**
 * Compact dashboard stats: work-order status breakdown, 7-day agent-turn
 * sparkline, device utilization. All in a single horizontal row of cards so
 * the dashboard's vertical real estate stays usable.
 */
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Briefcase,
  Cpu,
} from "lucide-react";
import { dashboardApi, type CompanyStats } from "../api/queries/companies";

const STATUS_TONE: Record<string, string> = {
  draft: "text-tertiary",
  planning: "text-accent",
  dispatched: "text-accent",
  in_progress: "text-status-running",
  complete: "text-status-running",
  failed: "text-status-error",
  cancelled: "text-tertiary",
};

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  planning: "Planning",
  dispatched: "Dispatched",
  in_progress: "Running",
  complete: "Complete",
  failed: "Failed",
  cancelled: "Cancelled",
};

export function StatsCards({ companyId }: { companyId: string }) {
  const q = useQuery({
    queryKey: ["stats", companyId],
    queryFn: () => dashboardApi.stats(companyId),
    refetchInterval: 15_000,
  });

  if (q.isLoading || !q.data) {
    return (
      <div className="grid grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="card h-[140px] grid place-items-center">
            <span className="spinner" aria-hidden="true" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <WorkOrderCard data={q.data} />
      <AgentTurnsCard data={q.data} />
      <DevicesCard data={q.data} />
    </div>
  );
}

function WorkOrderCard({ data }: { data: CompanyStats }) {
  const total = data.work_orders_by_status.reduce((s, x) => s + x.n, 0);
  const ordered = [...data.work_orders_by_status].sort((a, b) => b.n - a.n);

  return (
    <div className="card p-3">
      <div className="flex items-center gap-2 mb-2">
        <Briefcase size={14} className="text-tertiary" />
        <h3 className="text-[11px] font-medium tracking-wide uppercase text-tertiary">
          Work orders
        </h3>
      </div>
      <div className="text-[24px] font-semibold leading-none text-primary">
        {total}
      </div>
      <div className="text-[11px] text-tertiary mt-0.5">total</div>
      <ul className="mt-2 space-y-0.5 text-[11px]">
        {ordered.slice(0, 5).map((s) => (
          <li key={s.status} className="flex justify-between">
            <span className={STATUS_TONE[s.status] ?? "text-secondary"}>
              {STATUS_LABEL[s.status] ?? s.status}
            </span>
            <span className="font-mono text-secondary">{s.n}</span>
          </li>
        ))}
        {ordered.length === 0 && (
          <li className="text-tertiary">No orders yet</li>
        )}
      </ul>
    </div>
  );
}

function AgentTurnsCard({ data }: { data: CompanyStats }) {
  const totalCeo = data.agent_turns_week.find((r) => r.role === "ceo")?.n ?? 0;
  const totalOps = data.agent_turns_week.find((r) => r.role === "ops")?.n ?? 0;
  const maxPerDay = Math.max(
    1,
    ...data.agent_turns_by_day.map((d) => Math.max(d.ceo, d.ops)),
  );

  return (
    <div className="card p-3">
      <div className="flex items-center gap-2 mb-2">
        <Activity size={14} className="text-tertiary" />
        <h3 className="text-[11px] font-medium tracking-wide uppercase text-tertiary">
          Agent turns · 7d
        </h3>
      </div>
      <div className="flex gap-4 mb-2">
        <div>
          <div className="text-[20px] font-semibold leading-none text-accent">
            {totalCeo}
          </div>
          <div className="text-[11px] text-tertiary mt-0.5">CEO</div>
        </div>
        <div>
          <div className="text-[20px] font-semibold leading-none text-primary">
            {totalOps}
          </div>
          <div className="text-[11px] text-tertiary mt-0.5">Ops</div>
        </div>
      </div>
      <div className="flex items-end gap-[2px] h-[40px]">
        {data.agent_turns_by_day.map((d) => {
          const h = (val: number) => Math.max(2, (val / maxPerDay) * 36);
          const date = new Date(d.day);
          const label = date.toLocaleDateString(undefined, { weekday: "short" });
          return (
            <div
              key={d.day}
              className="flex-1 flex flex-col items-stretch gap-[1px] justify-end"
              title={`${label}: CEO ${d.ceo} · Ops ${d.ops}`}
            >
              <div
                className="bg-accent/70 rounded-sm"
                style={{ height: `${h(d.ceo)}px` }}
              />
              <div
                className="bg-secondary/40 rounded-sm"
                style={{ height: `${h(d.ops)}px` }}
              />
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-tertiary mt-1">
        Bars: accent = CEO, muted = Ops
      </div>
    </div>
  );
}

function DevicesCard({ data }: { data: CompanyStats }) {
  const totalSteps = data.steps_by_device.reduce((s, x) => s + x.n, 0);
  const top = [...data.steps_by_device].sort((a, b) => b.n - a.n).slice(0, 4);

  return (
    <div className="card p-3">
      <div className="flex items-center gap-2 mb-2">
        <Cpu size={14} className="text-tertiary" />
        <h3 className="text-[11px] font-medium tracking-wide uppercase text-tertiary">
          Devices
        </h3>
      </div>
      <div className="flex gap-4 mb-2">
        <div>
          <div className="text-[20px] font-semibold leading-none text-status-running">
            {data.devices_online}
          </div>
          <div className="text-[11px] text-tertiary mt-0.5">online</div>
        </div>
        <div>
          <div className="text-[20px] font-semibold leading-none text-primary">
            {data.devices_active}
          </div>
          <div className="text-[11px] text-tertiary mt-0.5">paired</div>
        </div>
      </div>
      <div className="text-[11px] text-tertiary mb-1">Top steps · all time</div>
      <ul className="space-y-0.5 text-[11px]">
        {top.map((d) => (
          <li key={d.device_id} className="flex justify-between gap-2">
            <span className="text-secondary truncate" title={d.device_name}>
              {d.device_name}
            </span>
            <span className="font-mono text-tertiary shrink-0">{d.n}</span>
          </li>
        ))}
        {top.length === 0 && (
          <li className="text-tertiary">
            No steps yet — total {totalSteps}
          </li>
        )}
      </ul>
    </div>
  );
}
